import React, { useState, useEffect } from 'react';
import { FaUsers, FaUserShield, FaUserEdit, FaUser, FaClock, FaComments, FaChartLine, FaChartBar, FaShieldAlt } from 'react-icons/fa';
import { useLanguage } from '../../i18n';
import MFASetup from '../MFASetup/MFASetup';
import './UserManager.css';

const STRANDS_API_URL = process.env.REACT_APP_STRANDS_API_URL || 'http://localhost:5001';

const UserManager = ({ username }) => {
  const { t, language } = useLanguage();
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState({});
  const [organization, setOrganization] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [periodDays, setPeriodDays] = useState(-1);
  const [mfaModalOpen, setMfaModalOpen] = useState(false);

  const periodOptions = [
    { value: -1, label: language === 'es' ? 'Mes en curso' : language === 'pt' ? 'Mês atual' : 'Current month' },
    { value: 15, label: language === 'es' ? 'Últimos 15 días' : language === 'pt' ? 'Últimos 15 dias' : 'Last 15 days' },
    { value: 30, label: language === 'es' ? 'Últimos 30 días' : language === 'pt' ? 'Últimos 30 dias' : 'Last 30 days' },
    { value: 60, label: language === 'es' ? 'Últimos 60 días' : language === 'pt' ? 'Últimos 60 dias' : 'Last 60 days' },
    { value: 90, label: language === 'es' ? 'Últimos 90 días' : language === 'pt' ? 'Últimos 90 dias' : 'Last 90 days' },
    { value: 0, label: language === 'es' ? 'Histórico completo' : language === 'pt' ? 'Histórico completo' : 'All time' },
  ];

  // Formatear tiempo relativo
  const formatRelativeTime = (dateString) => {
    if (!dateString) return t('admin.never');

    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffSec = Math.floor(diffMs / 1000);
    const diffMin = Math.floor(diffSec / 60);
    const diffHrs = Math.floor(diffMin / 60);
    const diffDays = Math.floor(diffHrs / 24);

    if (diffSec < 60) return t('admin.justNow');
    if (diffMin < 60) return `${diffMin} ${t('admin.minutesAgo')}`;
    if (diffHrs < 24) return `${diffHrs}${t('admin.hoursAgo')}`;
    if (diffDays === 1) return t('admin.yesterday');
    if (diffDays < 7) return `${diffDays} ${t('admin.daysAgo')}`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} ${t('admin.weeksAgo')}`;
    return formatDate(dateString);
  };

  const fetchUsers = async (days = periodDays) => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await fetch(
        `${STRANDS_API_URL}/api/admin/users?username=${encodeURIComponent(username)}&days=${days}`
      );

      if (!response.ok) {
        throw new Error('Error al cargar usuarios');
      }

      const data = await response.json();

      if (data.success) {
        setUsers(data.users || []);
        setStats(data.stats || {});
        setOrganization(data.organization);
      } else {
        throw new Error(data.error || 'Error desconocido');
      }
    } catch (err) {
      console.error('[UserManager] Error:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (username) {
      fetchUsers();
    }
  }, [username]);

  const handlePeriodChange = (e) => {
    const days = parseInt(e.target.value);
    setPeriodDays(days);
    fetchUsers(days);
  };

  const getRoleBadgeClass = (role) => {
    switch (role?.toLowerCase()) {
      case 'admin':
        return 'role-admin';
      case 'editor':
        return 'role-editor';
      default:
        return 'role-viewer';
    }
  };

  const getRoleIcon = (role) => {
    switch (role?.toLowerCase()) {
      case 'admin':
        return <FaUserShield />;
      case 'editor':
        return <FaUserEdit />;
      default:
        return <FaUser />;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString(language === 'es' ? 'es-MX' : language === 'pt' ? 'pt-BR' : 'en-US', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const currentPeriodLabel = periodOptions.find(o => o.value === periodDays)?.label || '';

  return (
    <div className="user-manager">
      {/* Header */}
      <div className="user-manager-header">
        <div className="header-title">
          <FaUsers className="header-icon" />
          <h3>{t('admin.usersTitle')}</h3>
        </div>
        <div className="header-actions">
          <button
            className="mfa-config-btn"
            onClick={() => setMfaModalOpen(true)}
            title={t('admin.configureMyMFA')}
          >
            <FaShieldAlt />
            <span>{t('admin.configureMyMFA')}</span>
          </button>
          <div className="period-selector">
            <FaClock className="period-icon" />
            <select value={periodDays} onChange={handlePeriodChange} className="period-select">
              {periodOptions.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {mfaModalOpen && <MFASetup onClose={() => setMfaModalOpen(false)} />}

      {/* Stats Cards */}
      <div className="stats-grid">
        <div className="stat-card total">
          <div className="stat-icon">
            <FaUsers />
          </div>
          <div className="stat-info">
            <span className="stat-value">{stats.total_users || 0}</span>
            <span className="stat-label">{t('admin.totalUsers')}</span>
          </div>
        </div>

        <div className="stat-card active7">
          <div className="stat-icon">
            <FaChartLine />
          </div>
          <div className="stat-info">
            <span className="stat-value">{stats.active_last_7_days || 0}</span>
            <span className="stat-label">{t('admin.active7Days')}</span>
          </div>
        </div>

        <div className="stat-card active30">
          <div className="stat-icon">
            <FaClock />
          </div>
          <div className="stat-info">
            <span className="stat-value">{stats.active_last_30_days || 0}</span>
            <span className="stat-label">{t('admin.active30Days')}</span>
          </div>
        </div>

        <div className="stat-card admin">
          <div className="stat-icon">
            <FaUserShield />
          </div>
          <div className="stat-info">
            <span className="stat-value">{stats.admin_count || 0}</span>
            <span className="stat-label">{t('admin.administrators')}</span>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="error-message">
          <span>{error}</span>
          <button onClick={() => fetchUsers()}>{t('admin.retry')}</button>
        </div>
      )}

      {/* Users Table */}
      <div className="users-list">
        {isLoading ? (
          <div className="users-loading">
            <div className="spinner"></div>
            <p>{t('admin.loadingUsers')}</p>
          </div>
        ) : users.length === 0 ? (
          <div className="no-users">
            <FaUsers className="no-users-icon" />
            <p>{t('admin.noUsersInOrg')}</p>
          </div>
        ) : (
          <div className="users-table-container">
            <table className="users-table">
              <thead>
                <tr>
                  <th>{t('admin.username')}</th>
                  <th>{t('admin.role')}</th>
                  <th>{t('admin.lastAccess')}</th>
                  <th>{t('admin.pbiAccess')}</th>
                  <th>{t('admin.messages')}</th>
                  <th>{t('admin.registrationDate')}</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user, index) => (
                  <tr key={index} className={user.last_login && (new Date() - new Date(user.last_login)) < 7 * 24 * 60 * 60 * 1000 ? 'active-user' : ''}>
                    <td className="user-cell">
                      <span className="user-avatar">
                        {user.username?.charAt(0)?.toUpperCase()}
                      </span>
                      <span className="user-name">{user.username}</span>
                    </td>
                    <td>
                      <span className={`role-badge ${getRoleBadgeClass(user.role)}`}>
                        {getRoleIcon(user.role)}
                        {user.role}
                      </span>
                    </td>
                    <td className="last-login-cell">
                      <span className={`last-login ${user.last_login ? 'has-login' : 'no-login'}`}>
                        {formatRelativeTime(user.last_login)}
                      </span>
                    </td>
                    <td className="powerbi-cell">
                      <span className="powerbi-count">
                        <FaChartBar className="pbi-icon" />
                        {user.pbi_count || 0}
                      </span>
                    </td>
                    <td className="messages-cell">
                      <span className="message-count">
                        <FaComments className="msg-icon" />
                        {user.messages_count || 0}
                      </span>
                    </td>
                    <td className="date-cell">
                      {formatDate(user.created_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserManager;
